import { OptionType } from "@atlaskit/select";
import styled from "@emotion/styled";
import compose from "ramda/src/compose";
import map from "ramda/src/map";
import prepend from "ramda/src/prepend";
import find from "ramda/src/find";
import path from "ramda/src/path";
import pathEq from "ramda/src/pathEq";
import Stack from "@mui/material/Stack";
import { FC, useEffect, useMemo, useState } from "react";
import { URL } from "../../constants/navigation";
import { COLOR_NILE_BLUE, COLOR_SCIENCE_BLUE } from "../../constants/styles";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import { usePlacementWorksheetContext } from "../../contexts/PlacementWorksheetContext";
import { useNetwork } from "../../hooks/useNetwork";
import { useSnackbar } from "../../hooks/useSnackbar";
import useAuth from "../../routes/login/AuthContext";
import {
  deletePlacementWorksheetRoomOccupant,
  putPlacementWorksheetOccupantRoom,
} from "../../services/PlacementWorksheetApi";
import { Button } from "../../UI/Button";
import { Divider } from "../../UI/Divider";
import { Grid } from "../../UI/Grid";
import { Popover } from "../../UI/Popover";
import { Typography } from "../../UI/Typography";
import { usePlacementWorksheetTableRowContext } from "../PlacementWorksheetTable/PlacementWorksheetTableRowContext";
import { UnmatchButton } from "./UnmatchButton";
import { ModalProvider } from "../../contexts/ModalContext";
import { Select } from "../../UI/Select";
import { normalize } from "../../helpers/normalize";
import { rejectIsNil } from "../../helpers/rejectIsNil";
import { pathOrArray } from "../../helpers/pathOrArray";

interface IResidentMenuProps {
  title?: string;
  unitId: number;
  l4Id?: number | null;
  selectedRoomOption?: OptionType | null;
  roomOptions?: OptionType[];
  open: boolean;
  residentId?: string;
  anchorEl?: Element | null;
  roomLabelId?: number;
  onClose?: () => void;
}

export const StyledButton = styled(Button)`
  span {
    text-align: left;
    color: ${COLOR_SCIENCE_BLUE};
    font-weight: 400;
  }
`;

const StyledRoomButton = styled(Button)`
  width: 100% !important;
  span {
    width: 100%;
  }
`;

const findUnit = (unitId: number) =>
  compose(find(pathEq(["unitId"], unitId)), pathOrArray(["assignedUnits"]));

const getRoomLabelId = compose(
  rejectIsNil,
  map(path(["roomLabelId"])),
  pathOrArray(["occupants"])
);

const getOccupatedUnitRooms = (unitId: number) =>
  compose<any, any, any>(getRoomLabelId as any, findUnit(unitId));

const prependUnspecifiedOption = prepend<OptionType>({
  value: "clear",
  label: "Unspecified",
});

export const ResidentMenu: FC<IResidentMenuProps> = ({
  title,
  open,
  roomLabelId,
  unitId,
  anchorEl,
  l4Id,
  roomOptions = [],
  residentId,
  selectedRoomOption,
  onClose,
}) => {
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { showErrorSnackbar } = useSnackbar();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { getL4, getL3 } = useNetwork();
  const [isSaveRoomLoading, setIsSaveRoomLoading] = useState(false);
  const { l2Id, l3Id, updateUnitOccupantRoomLabelId, occupants } =
    usePlacementWorksheetContext();
  const { setIsFinished } = usePlacementWorksheetTableRowContext();
  const [selectedRoom, setSelectedRoom] = useState<OptionType | null>(null);

  useEffect(() => {
    if (selectedRoomOption) {
      setSelectedRoom(selectedRoomOption);
    }
  }, [normalize(selectedRoomOption)]);

  const handleSelectRoom = (selection: any) => {
    setSelectedRoom(selection);
  };

  const handleViewProfileClick = () => {
    if (!residentId) return;

    window.open(URL.USER.replace(":id", residentId), "_blank");
  };

  const handleRoomRemove = async () => {
    if (!l2Id || !l3Id || !residentId || !roomLabelId) return;

    await deletePlacementWorksheetRoomOccupant(
      selectedNetworkId,
      l2Id,
      l3Id,
      unitId,
      residentId,
      roomLabelId,
      await getAuthToken()
    )
      .then((res: any) => {
        if (res.error) {
          showErrorSnackbar(res.error.description);
        }

        updateUnitOccupantRoomLabelId(unitId, residentId, null);
        setIsFinished(true);

        if (onClose) {
          onClose();
        }
      })
      .catch(() => showErrorSnackbar());
  };

  const l4 = useMemo(() => {
    if (!selectedNetworkId || !l2Id || !l3Id || !l4Id) return;

    return getL4(selectedNetworkId, l2Id, l3Id, l4Id);
  }, [selectedNetworkId, l2Id, l3Id, l4Id, getL4]);

  const l3 = useMemo(() => {
    if (!selectedNetworkId || !l2Id || !l3Id) return;

    return getL3(selectedNetworkId, l2Id, l3Id);
  }, [selectedNetworkId, l2Id, l3Id, getL3]);

  const occupatedRooms = occupants ? getOccupatedUnitRooms(unitId)(occupants) : [];

  const getIsOptionDisabled = (option: OptionType) =>
    option && option.value ? occupatedRooms.includes(Number(option.value)) : false;

  const isAllRoomsOcupated = (occupatedRooms?.length || 0) === (roomOptions?.length || 0);

  const handleRoomSave = async () => {
    if (!l2Id || !l3Id || !residentId || !selectedRoom?.value) return;

    setIsSaveRoomLoading(true);

    if (selectedRoom.value === "clear") {
      await handleRoomRemove();
    } else {
      await putPlacementWorksheetOccupantRoom(
        selectedNetworkId,
        l2Id,
        l3Id,
        unitId,
        residentId,
        selectedRoom.value,
        await getAuthToken()
      )
        .then((res: any) => {
          if (res.error) {
            showErrorSnackbar(res.error.description);
          }

          updateUnitOccupantRoomLabelId(
            unitId,
            residentId,
            Number(selectedRoom.value)
          );

          setIsFinished(true);

          if (onClose) {
            onClose();
          }
        })
        .catch(() => showErrorSnackbar());
    }

    setIsSaveRoomLoading(false);
  };

  const selectOptions = roomLabelId
    ? prependUnspecifiedOption(roomOptions)
    : roomOptions;

  return (
    <Popover
      id="choose-r-popover"
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Stack
        sx={{
          maxWidth: 266,
          margin: "8px 4px 4px 8px",
        }}
      >
        {title && (
          <Typography
            gutterBottom={!!l3 && !!l4}
            textAlign="center"
            variant="h6"
          >
            {title}
          </Typography>
        )}
        {l3 && l4 && (
          <Typography gutterBottom textAlign="center" variant="subtitle1">
            ({l3.name} {l4.name})
          </Typography>
        )}
        <StyledButton appearance="subtle" onClick={handleViewProfileClick}>
          View profile
        </StyledButton>
        <ModalProvider>
          <UnmatchButton
            unitId={unitId}
            onSuccess={onClose}
            residentId={residentId}
          />
        </ModalProvider>
        <Divider sx={{ my: 1 }} />
        <Grid container alignItems="center" spacing={1} p={1}>
          <Grid item>
            <Typography color={COLOR_NILE_BLUE}>Room:</Typography>
          </Grid>
          <Grid item xs>
            <Select
              value={selectedRoom}
              onChange={handleSelectRoom}
              menuPosition="fixed"
              options={selectOptions}
              isOptionDisabled={getIsOptionDisabled}
            />
          </Grid>
          <Grid item xs={12}>
            <StyledRoomButton
              onClick={handleRoomSave}
              isLoading={isSaveRoomLoading}
              isDisabled={
                !selectedRoom || isAllRoomsOcupated || isSuperAdminRead()
              }
            >
              Save
            </StyledRoomButton>
          </Grid>
        </Grid>
      </Stack>
    </Popover>
  );
};
